/**
 * Landing page "Get Lumioos on every device" section.
 *
 * Three platform cards. Each card's state is driven by an admin-controlled
 * feature flag fetched via /public/feature-flags so the admin can switch a
 * store between `enabled` (live link), `coming_soon` (muted badge) or
 * `hidden` (card removed) without a deploy.
 *
 * Flag keys (registered in backend server.py `KNOWN_FEATURES`):
 *   - download_microsoft → default enabled
 *   - download_apple     → default coming_soon
 *   - download_android   → default coming_soon
 *
 * Admin flips them at /app/admin → Feature Flags.
 */

import { Sparkles } from "lucide-react";
import { useT } from "@/i18n";

// Official Apple App Store glyph — rounded-square with the white triple-stroke
// "A" inside the iconic light-blue → deep-blue gradient. Stylised from the
// Apple HIG App Store badge. No image asset needed.
const AppleAppStoreIcon = () => (
  <svg viewBox="0 0 200 200" className="w-9 h-9" aria-hidden="true">
    <defs>
      <linearGradient id="appstore-bg" x1="0%" y1="0%" x2="0%" y2="100%">
        <stop offset="0%" stopColor="#1AC8FB" />
        <stop offset="100%" stopColor="#0E70F8" />
      </linearGradient>
    </defs>
    <rect x="0" y="0" width="200" height="200" rx="46" fill="url(#appstore-bg)" />
    {/* The white "A" — three rounded strokes converging at the apex */}
    <g
      fill="none"
      stroke="#FFFFFF"
      strokeWidth="14"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      {/* left leg */}
      <path d="M70 142 L100 70" />
      {/* right leg */}
      <path d="M100 70 L130 142" />
      {/* crossbar with a tiny notch (the App Store "A" leaves a gap at center) */}
      <path d="M82 124 L106 124" />
      <path d="M120 124 L124 124" />
    </g>
  </svg>
);

const MicrosoftStoreIcon = () => (
  <svg viewBox="0 0 24 24" className="w-9 h-9" aria-hidden="true">
    <rect x="1" y="1" width="10" height="10" fill="#F25022" />
    <rect x="13" y="1" width="10" height="10" fill="#7FBA00" />
    <rect x="1" y="13" width="10" height="10" fill="#00A4EF" />
    <rect x="13" y="13" width="10" height="10" fill="#FFB900" />
  </svg>
);

const GooglePlayIcon = () => (
  <svg viewBox="0 0 24 24" className="w-9 h-9" aria-hidden="true">
    <defs>
      <linearGradient id="gp1" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#00D2FF" />
        <stop offset="100%" stopColor="#0091EA" />
      </linearGradient>
      <linearGradient id="gp2" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#FFEC00" />
        <stop offset="100%" stopColor="#FFA000" />
      </linearGradient>
      <linearGradient id="gp3" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#FF3D44" />
        <stop offset="100%" stopColor="#D32F2F" />
      </linearGradient>
      <linearGradient id="gp4" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#00C853" />
        <stop offset="100%" stopColor="#1B5E20" />
      </linearGradient>
    </defs>
    <path d="M3.6 1.4l11.2 10.6L3.6 22.6c-.4-.2-.6-.6-.6-1.1V2.5c0-.5.2-.9.6-1.1z" fill="url(#gp1)" />
    <path d="M14.8 12L18.9 8l3.4 2c.6.3.6 1.2 0 1.5l-3.4 2-4.1-1.5z" fill="url(#gp2)" />
    <path d="M3.6 1.4L18.9 8l-4.1 4-11.2-10.6z" fill="url(#gp4)" />
    <path d="M3.6 22.6l11.2-10.6 4.1 4L3.6 22.6z" fill="url(#gp3)" />
  </svg>
);

// Single source of truth for the 3 stores. To add a new store later,
// drop a new entry — the rendering logic just iterates.
const STORES = [
  {
    id: "microsoft",
    flagKey: "download_microsoft",
    name: "Microsoft Store",
    platform: "Windows",
    sub: "Windows 10 & 11",
    url: "https://apps.microsoft.com/detail/9P315QMPK95Z",
    Icon: MicrosoftStoreIcon,
  },
  {
    id: "apple",
    flagKey: "download_apple",
    name: "App Store",
    platform: "iOS",
    sub: "iPhone & iPad",
    url: null,  // populate when launch URL is ready
    Icon: AppleAppStoreIcon,
  },
  {
    id: "android",
    flagKey: "download_android",
    name: "Google Play",
    platform: "Android",
    sub: "Android",
    url: null,  // populate when launch URL is ready
    Icon: GooglePlayIcon,
  },
];

export default function DownloadSection({ flags = {} }) {
  const { t } = useT();

  // Filter + classify each store from the flag state.
  // Missing flag (e.g. backend down) defaults to `enabled` so we never
  // accidentally hide the Windows install link.
  const visible = STORES
    .map((s) => ({ ...s, state: flags[s.flagKey] || "enabled" }))
    .filter((s) => s.state !== "hidden");

  // If admin hid ALL three (unusual), drop the section entirely so we
  // don't render an empty grid with just a heading.
  if (visible.length === 0) return null;

  // Heading adapts so it always reads correctly:
  //   1 store visible  → "Lumioos is now on Windows"   (uses the live store's platform name)
  //   2 stores visible → "Lumioos for Windows & iOS"   (joins both platform names)
  //   3 stores visible → "Get Lumioos on every device" (default — works only when ALL are open)
  let heading;
  let subheading;
  if (visible.length === 1) {
    const p = visible[0].platform;
    heading =
      t("lp_download_h2_one", { platform: p }) || `Lumioos is now on ${p}`;
    subheading =
      t("lp_download_sub_one", { platform: p }) ||
      `Install Lumioos natively on ${p}. More platforms coming soon.`;
  } else if (visible.length === 2) {
    const p1 = visible[0].platform;
    const p2 = visible[1].platform;
    heading =
      t("lp_download_h2_two", { p1, p2 }) || `Lumioos for ${p1} & ${p2}`;
    subheading =
      t("lp_download_sub_two", { p1, p2 }) ||
      `Install on ${p1} or ${p2} — more devices coming soon.`;
  } else {
    heading = t("lp_download_h2") || "Get Lumioos on every device";
    subheading =
      t("lp_download_sub") ||
      "Install Lumioos natively on Windows now — Apple and Android coming soon.";
  }

  return (
    <section
      id="download"
      className="border-b border-amber-100 bg-white"
      data-testid="landing-download-section"
    >
      <div className="max-w-7xl mx-auto px-6 py-24">
        <div className="text-center max-w-2xl mx-auto">
          <div className="text-xs font-semibold uppercase tracking-[0.2em] text-orange-600">
            {t("lp_download_eyebrow") || "Download"}
          </div>
          <h2 className="mt-4 text-4xl sm:text-5xl font-black tracking-tighter">
            {heading}
          </h2>
          <p className="mt-4 text-lg text-zinc-600">
            {subheading}
          </p>
        </div>

        {/* Layout adapts so cards stay the SAME width regardless of how
            many are visible — hiding a store doesn't stretch the
            remaining cards into awkward giant blocks. We use a flex row
            with a fixed-width per card; rows wrap naturally on smaller
            screens. */}
        <div
          className="mt-12 flex flex-wrap justify-center gap-5 max-w-5xl mx-auto"
          data-testid="download-cards"
        >
          {visible.map((s) => {
            const isLive = s.state === "enabled" || s.state === "new";
            const Wrapper = isLive && s.url ? "a" : "div";
            const wrapperProps =
              isLive && s.url
                ? {
                    href: s.url,
                    target: "_blank",
                    rel: "noopener noreferrer",
                    "data-testid": `download-card-${s.id}`,
                  }
                : { "data-testid": `download-card-${s.id}-soon` };

            return (
              <Wrapper
                key={s.id}
                {...wrapperProps}
                className={`group relative block rounded-2xl border w-full sm:w-[320px] ${
                  isLive
                    ? "border-zinc-200 hover:border-zinc-900 bg-white hover:shadow-2xl"
                    : "border-zinc-200 bg-zinc-50/60 cursor-default"
                } p-6 transition-all duration-200`}
              >
                <div className="flex items-start justify-between">
                  <div
                    className={`w-16 h-16 rounded-2xl bg-white border border-zinc-200 flex items-center justify-center ${
                      isLive ? "shadow-sm group-hover:scale-105 transition-transform" : ""
                    }`}
                  >
                    <s.Icon />
                  </div>
                  {isLive ? (
                    <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700 text-[10px] font-black uppercase tracking-wider">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                      {t("lp_download_live") || "Live"}
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-amber-50 border border-amber-200 text-amber-800 text-[10px] font-black uppercase tracking-wider">
                      <Sparkles className="w-2.5 h-2.5" />
                      {t("lp_download_soon") || "Coming soon"}
                    </span>
                  )}
                </div>

                <div className="mt-5">
                  <div
                    className={`text-2xl font-black tracking-tight ${
                      isLive ? "text-zinc-950" : "text-zinc-400"
                    }`}
                  >
                    {s.name}
                  </div>
                  <div
                    className={`mt-1 text-sm ${
                      isLive ? "text-zinc-500" : "text-zinc-400"
                    }`}
                  >
                    {s.sub}
                  </div>
                </div>

                <div className="mt-6 pt-5 border-t border-zinc-100">
                  {isLive ? (
                    <span className="inline-flex items-center gap-2 text-sm font-bold text-zinc-950 group-hover:text-orange-600 transition-colors">
                      {t("lp_download_install") || "Install free"}
                      <span className="transition-transform group-hover:translate-x-1">→</span>
                    </span>
                  ) : (
                    <span className="text-sm font-semibold text-zinc-400">
                      {t("lp_download_notify") || "We'll launch soon"}
                    </span>
                  )}
                </div>
              </Wrapper>
            );
          })}
        </div>
      </div>
    </section>
  );
}
